import { produce } from "immer";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { errorAlertMessageFrom } from "../../error";
import {
  UPDATE_MATTER_COLLABORATORS,
  UPDATE_PARENT_MATTER_COLLABORATORS,
} from "../../graphql/mutation";
import { MATTER_COLLABORATORS_QUERY } from "../../graphql/query/matter-collaborators-query";
import {
  FindMatterCollaboratorUserIds,
  FindMatterCollaboratorUserIdsVariables,
  FindMatterCollaboratorUserIds_matterCollaborators,
} from "../../graphql/__generated_types__/FindMatterCollaboratorUserIds";
import { AlertMessage, newAlertMessage, ShareMatterStatus } from "@design-system";
export interface ShareMatterViewModel {
  loading(): boolean;
  status(): ShareMatterStatus | undefined;
  collaboratorUsers(): { id: number; displayName: string }[] | undefined;
  share(data: { userIds: number[]; message?: string }, isParent: boolean): void;
  alertMessage(): AlertMessage | undefined;
  removeCollaborator(userId: number, isParent: boolean): void;
}

type ViewState = {
  status?: ShareMatterStatus;
  alertMessage?: AlertMessage;
  actionType?: "share" | "removeCollaborator";
};
const INITIAL_STATE: ViewState = {};

export const useShareMatterViewModel = (matterId: number): ShareMatterViewModel => {
  const [viewState, setViewState] = useState<ViewState>(INITIAL_STATE);
  const { status, alertMessage, actionType } = viewState;

  const { data: collaboratorsData, loading: collaboratorsLoading } = useQuery<
    FindMatterCollaboratorUserIds,
    FindMatterCollaboratorUserIdsVariables
  >(MATTER_COLLABORATORS_QUERY, {
    variables: { matterId },
    onError: (err) =>
      setViewState({
        ...viewState,
        alertMessage: errorAlertMessageFrom(err, "Failed to load matter collaborators"),
      }),
  });

  const [updateMatterCollaborators] = useMutation(UPDATE_MATTER_COLLABORATORS, {
    onCompleted: () => {
      const successMessage =
        actionType === "share" ? "Shared with collaborators" : "Removed collaborator";
      setViewState((prevState) => ({
        ...prevState,
        status: "SHARING_COMPLETED",
        alertMessage: newAlertMessage("success", successMessage),
      }));
    },
    onError: (err) =>
      setViewState({
        ...viewState,
        alertMessage: errorAlertMessageFrom(err, "Failed to share with collaborators"),
      }),
    update: (cache, { data: newData }) => {
      cache.updateQuery(
        { query: MATTER_COLLABORATORS_QUERY, variables: { matterId } },
        (currentData) => {
          const newMatterCollaborators =
            newData?.updateMatterCollaborators as FindMatterCollaboratorUserIds_matterCollaborators[];
          return produce(currentData, (draft) => {
            if (!draft) {
              draft = {
                matterCollaborators: newMatterCollaborators,
              };
            } else {
              draft.matterCollaborators = newMatterCollaborators;
            }
          });
        }
      );
    },
  });

  const [updateParentMatterCollaborators] = useMutation(UPDATE_PARENT_MATTER_COLLABORATORS, {
    onCompleted: () =>
      setViewState({
        status: "SHARING_COMPLETED",
        alertMessage: newAlertMessage("success", "Shared with collaborators"),
      }),
    onError: (err) =>
      setViewState({
        ...viewState,
        alertMessage: errorAlertMessageFrom(err, "Failed to share with collaborators"),
      }),
  });

  const loading = collaboratorsLoading;

  return useMemo(
    () => ({
      loading: () => loading,
      status: () => status,
      collaboratorUsers: () => collaboratorsData?.matterCollaborators.map((mc) => mc.user),
      share: ({ userIds, message }, isParent) => {
        setViewState({
          ...viewState,
          status: "SHARING",
          actionType: "share",
        });
        if (isParent) {
          updateParentMatterCollaborators({
            variables: { data: { matterId, userIds, message } },
          });
          return;
        }
        updateMatterCollaborators({ variables: { data: { matterId, userIds, message } } });
      },
      removeCollaborator: (userId: number, isParent: boolean) => {
        setViewState({
          ...viewState,
          status: "SHARING",
          actionType: "removeCollaborator",
        });
        const newUserIds = collaboratorsData?.matterCollaborators
          .map((mc) => mc.user.id)
          .filter((id) => id !== userId);
        if (!newUserIds) return;
        if (isParent) {
          updateParentMatterCollaborators({
            variables: { data: { matterId, userIds: newUserIds } },
          });
          return;
        }
        updateMatterCollaborators({ variables: { data: { matterId, userIds: newUserIds } } });
      },
      alertMessage: () => alertMessage,
    }),
    [matterId, status, collaboratorsData, updateMatterCollaborators, alertMessage]
  );
};
